import type { ComponentConfig } from '@measured/puck';
import { motion } from 'framer-motion';
import { ArrowRight, ClipboardList, Sparkles } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { easeStripe } from '../../lib/motion';

export interface HeroDiagnosticProps {
  title: string;
  subtitle?: string;
}

const STEPS = [
  { label: 'Numéro SIRET (identifiant entreprise)', code: '01' },
  { label: 'Secteur d’activité', code: '02' },
  { label: '5 questions', code: '03' },
  { label: 'Estimation chiffrée', code: '04' },
] as const;

export function HeroDiagnostic({ title, subtitle }: HeroDiagnosticProps) {
  return (
    <section className="relative overflow-hidden bg-paper">
      {/* Background subtil */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0 opacity-70" />
        <div className="spot-glow" />
      </div>

      <Container className="relative py-16 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[1.05fr,1fr] lg:items-center lg:gap-14">
          {/* Left — copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: easeStripe }}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur"
            >
              <span className="live-dot" aria-hidden />
              <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                DIAG
              </span>
              <span className="h-3 w-px bg-stone-200" aria-hidden />
              <span>Diagnostic 60 sec</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.06, ease: easeStripe }}
              className="mt-6 text-balance font-display text-[clamp(2rem,4.5vw,3rem)] font-bold leading-[1.05] tracking-[-0.03em] text-ink"
            >
              {title}
            </motion.h1>

            {subtitle ? (
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.14, ease: easeStripe }}
                className="mt-5 max-w-prose text-[16px] leading-relaxed text-stone-600"
              >
                {subtitle}
              </motion.p>
            ) : null}
          </div>

          {/* Right — Live card 4-steps */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18, ease: easeStripe }}
            className="relative"
          >
            <div
              aria-hidden
              className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-brand-100/30 via-paper to-accent-100/20 blur-3xl"
            />

            <div className="live-card p-6 md:p-7">
              <div className="flex items-center gap-2 border-b border-stone-100 pb-4">
                <ClipboardList className="h-3.5 w-3.5 text-brand-600" strokeWidth={2} />
                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                  Flow, 4 étapes
                </span>
                <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] font-semibold text-brand-700">
                  <Sparkles className="h-2.5 w-2.5" strokeWidth={2.4} />
                  60s
                </span>
              </div>

              <ol className="mt-4 space-y-2">
                {STEPS.map((step, i) => (
                  <motion.li
                    key={step.code}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 0.3 + i * 0.07, ease: easeStripe }}
                    className="group flex items-center gap-3 rounded-md border border-stone-100 bg-stone-50/40 px-3 py-2.5 transition-colors hover:border-stone-200 hover:bg-stone-50"
                  >
                    <span className="font-mono text-[10px] font-semibold tabular-nums text-stone-400">
                      {step.code}
                    </span>
                    <span className="text-[13px] font-medium text-ink">{step.label}</span>
                    <ArrowRight
                      className="ml-auto h-3 w-3 text-stone-300 transition-all group-hover:translate-x-0.5 group-hover:text-stone-500"
                      strokeWidth={2}
                    />
                  </motion.li>
                ))}
              </ol>

              <Button asChild size="lg" variant="primary" className="group mt-5 w-full">
                <a href="/diagnostic">
                  Démarrer le diagnostic
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                </a>
              </Button>

              <p className="mt-3 text-center font-mono text-[10.5px] text-stone-500">
                Sans engagement, Gratuit, Aucune carte requise
              </p>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

export const HeroDiagnosticConfig: ComponentConfig<HeroDiagnosticProps> = {
  fields: {
    title: { type: 'textarea' },
    subtitle: { type: 'textarea' },
  },
  defaultProps: {
    title: 'Quelles aides pour vos travaux d’économie d’énergie ?',
    subtitle:
      "Saisissez votre SIRET. Notre moteur croise le registre officiel des entreprises et les barèmes en vigueur pour estimer vos primes CEE en moins d'une minute.",
  },
  render: HeroDiagnostic,
};
